import React, { useEffect, useState, useCallback } from "react";
import { api, fmtDate, fmt, todayISO, formatApiError } from "../lib/api";
import { FileText, CheckCircle, Circle, RefreshCw, ChevronDown, ChevronUp, Truck } from "lucide-react";

const TYPE_COLORS = {
  sale:       "bg-green-50 text-green-700 border-green-200",
  purchase:   "bg-blue-50 text-blue-700 border-blue-200",
  transfer:   "bg-purple-50 text-purple-700 border-purple-200",
  adjustment: "bg-yellow-50 text-yellow-700 border-yellow-200",
};
const TYPE_LABELS = { sale: "Sale", purchase: "Purchase", transfer: "Transfer", adjustment: "Adjustment" };

function TypeBadge({ type }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${TYPE_COLORS[type] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
      {TYPE_LABELS[type] || type}
    </span>
  );
}

function VerifyButton({ row, onVerify, loading }) {
  if (row.type !== "sale") return null;
  return (
    <button onClick={() => onVerify(row.id, !row.challan_verified)} disabled={loading}
      className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg font-medium transition-colors border disabled:opacity-50 ${
        row.challan_verified
          ? "bg-green-50 text-green-700 border-green-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
          : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-green-50 hover:text-green-700 hover:border-green-200"
      }`}
      data-testid={`verify-challan-${row.id}`}>
      {row.challan_verified
        ? <><CheckCircle size={11} /> Verified</>
        : <><Circle size={11} /> Verify</>}
    </button>
  );
}

export function ChallansPage() {
  const [rows, setRows]       = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [error, setError]     = useState("");

  // filters
  const [typeFilter, setTypeFilter] = useState("");
  const [dateFrom, setDateFrom]     = useState("");
  const [dateTo, setDateTo]         = useState(todayISO());
  const [verifiedFilter, setVerifiedFilter] = useState("");
  const [search, setSearch]         = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "2000" });
      if (typeFilter) params.append("type", typeFilter);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (verifiedFilter) params.append("verified", verifiedFilter);
      const r = await api.get(`/challans?${params}`);
      setRows(r.data?.rows || []);
      setSummary(r.data?.summary || null);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  }, [typeFilter, dateFrom, dateTo, verifiedFilter]);

  useEffect(() => { load(); }, [load]);

  const verify = async (id, verified) => {
    setVerifying(id);
    try {
      await api.post(`/challans/${id}/verify`, { verified });
      setRows(prev => prev.map(r => r.id === id ? { ...r, challan_verified: verified } : r));
    } catch (err) { setError(formatApiError(err)); }
    finally { setVerifying(""); }
  };

  const filtered = rows.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.challan_no || "").toLowerCase().includes(q)
        || (r.party_name || "").toLowerCase().includes(q)
        || (r.vehicle_no || "").toLowerCase().includes(q);
  });

  return (
    <div className="px-6 py-6 max-w-7xl">
      <div className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1 flex items-center gap-1.5">
          <FileText size={11} /> Transactions
        </div>
        <h1 className="font-black text-3xl tracking-tight">Challans</h1>
        <p className="text-sm text-gray-500 mt-1">All challans across sales, purchases &amp; transfers</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex justify-between">
          <span>{error}</span><button onClick={() => setError("")} className="font-bold ml-4">×</button>
        </div>
      )}

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Total Challans", val: summary.count },
            { label: "Total KG", val: `${fmt(summary.total_kg, 0)} kg` },
            { label: "Verified", val: summary.verified_count },
            { label: "Unverified", val: summary.unverified_count },
          ].map(s => (
            <div key={s.label} className="bg-white border border-gray-200 rounded-xl p-4 text-center">
              <div className="text-2xl font-black text-gray-900">{s.val}</div>
              <div className="text-xs text-gray-400 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search challan/party/vehicle…"
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 min-w-[200px]" data-testid="challan-search" />
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="challan-type-filter">
          <option value="">All Types</option>
          <option value="sale">Sales</option>
          <option value="purchase">Purchases</option>
          <option value="transfer">Transfers</option>
          <option value="adjustment">Adjustments</option>
        </select>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="challan-date-from" />
        <span className="text-gray-400 text-sm">to</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
        <select value={verifiedFilter} onChange={e => setVerifiedFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500">
          <option value="">All Status</option>
          <option value="true">Verified</option>
          <option value="false">Unverified</option>
        </select>
        <button onClick={load} className="p-2 hover:bg-gray-100 rounded-lg border border-gray-200" title="Refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="challans-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {["","Date","Challan No","Type","Party","From","To","MT","Filled","KG","Amount ₹","Vehicle","Verified",""].map((h, i) => (
                  <th key={i} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 500).map(row => (
                <React.Fragment key={row.id}>
                  <tr className={`border-b border-gray-50 hover:bg-gray-50/50 ${row.challan_verified ? "bg-green-50/20" : ""}`}>
                    <td className="px-2 py-2.5">
                      <button onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                        className="text-gray-400 hover:text-gray-600">
                        {expanded === row.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                      </button>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-gray-600">{fmtDate(row.date)}</td>
                    <td className="px-3 py-2.5 font-mono font-semibold text-gray-800">{row.challan_no || <span className="text-gray-300">—</span>}</td>
                    <td className="px-3 py-2.5"><TypeBadge type={row.type} /></td>
                    <td className="px-3 py-2.5 text-gray-700 max-w-[140px] truncate">{row.party_name || <span className="text-gray-300">—</span>}</td>
                    <td className="px-3 py-2.5 text-gray-500 text-xs">{row.from_godown || "—"}</td>
                    <td className="px-3 py-2.5 text-gray-500 text-xs">{row.to_godown || "—"}</td>
                    <td className="px-3 py-2.5 font-mono text-gray-700">{row.mt_qty}</td>
                    <td className="px-3 py-2.5 font-mono text-gray-700">{row.filled_qty}</td>
                    <td className="px-3 py-2.5 font-mono text-gray-700">{fmt(row.total_kg, 0)}</td>
                    <td className="px-3 py-2.5 font-mono text-right font-semibold text-gray-800">{row.total_amount > 0 ? `₹${fmt(row.total_amount)}` : "—"}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-500 font-mono">{row.vehicle_no}</td>
                    <td className="px-3 py-2.5">
                      <VerifyButton row={row} onVerify={verify} loading={verifying === row.id} />
                    </td>
                    <td className="px-3 py-2.5">
                      {row.challan_verified && <span title="Verified by" className="text-xs text-gray-400">{row.challan_verified_by}</span>}
                    </td>
                  </tr>
                  {expanded === row.id && (
                    <tr className="bg-gray-50">
                      <td colSpan={14} className="px-6 py-3">
                        <div className="flex flex-wrap gap-4 text-xs text-gray-600">
                          <div><span className="text-gray-400">Driver:</span> {row.driver_name || "—"}</div>
                          {row.challan_verified && <div><span className="text-gray-400">Verified at:</span> {fmtDate(row.challan_verified_at)}</div>}
                          <div><span className="font-semibold text-gray-700">Items:</span></div>
                          {(row.items || []).map((it, j) => (
                            <span key={j} className="bg-white border border-gray-200 rounded-lg px-2 py-1">
                              {it.brand} {it.size} — F:{it.filled_qty} MT:{it.mt_qty}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {filtered.length === 0 && !loading && (
                <tr><td colSpan={14} className="px-4 py-8 text-center text-sm text-gray-400">No challans found</td></tr>
              )}
              {loading && (
                <tr><td colSpan={14} className="px-4 py-8 text-center text-sm text-gray-400">Loading challans…</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > 500 && (
          <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">Showing first 500 of {filtered.length} challans</div>
        )}
      </div>
    </div>
  );
}

export default ChallansPage;
